/**
 * HTML Layer for canvas node rendering.
 *
 * Renders nodes as positioned div elements using CSS transforms for pan/zoom.
 * Handles DOM pooling and efficient updates.
 *
 * Key architecture:
 * - Nodes are positioned in WORLD coordinates (not screen)
 * - A single CSS transform on the container handles camera pan/zoom
 * - This is GPU-accelerated and allows text to scale properly with zoom
 * - Node content is rendered by type-specific renderers with LOD support
 *
 * Performance optimizations:
 * - Only updates nodes that have actually changed (dirty tracking)
 * - Skips DOM updates during pan-only operations
 * - Batches style updates to avoid layout thrashing
 */

import type { Camera, UINode, ViewportSize, ScreenMetrics } from "./types";
import {
  rendererRegistry,
  createHTMLLayerAccess,
  createSVGLayerAccess,
  createWebGLLayerAccess,
  type RenderLayers,
  type RenderOptions,
  type HTMLLayerAccess,
  type SVGLayerAccess,
  type WebGLLayerAccess,
  type LODLevel,
} from "./renderer";
import type { WebGLLayer } from "./webgl-layer";
import {
  RenderScheduler,
  createRenderScheduler,
  type NodeRenderState,
} from "./render-scheduler";

/** CSS class names used by the HTML layer */
const CSS = {
  layer: "canvas-html-layer",
  transform: "canvas-transform-layer",
  node: "canvas-node",
  selected: "selected",
  hovered: "hovered",
  dragging: "dragging",
  resizing: "resizing",
  rotating: "rotating",
} as const;

/**
 * HTML Layer manages DOM elements for canvas nodes.
 *
 * Features:
 * - Single CSS transform for camera (GPU-accelerated)
 * - Nodes positioned in world coordinates
 * - Efficient DOM pooling (reuse elements)
 * - Text scales naturally with zoom
 * - Dirty tracking to skip unchanged nodes
 */
export class HTMLLayer {
  private container: HTMLElement;
  private transformLayer: HTMLElement;
  private camera: Camera = { x: 0, y: 0, zoom: 1 };
  private viewport: ViewportSize = { width: 0, height: 0 };

  /** Map of node ID to DOM element */
  private nodeElements: Map<string, HTMLElement> = new Map();

  /** Render scheduler for tracking state and making render decisions */
  private scheduler: RenderScheduler = createRenderScheduler();

  /** Pool of reusable DOM elements */
  private elementPool: HTMLElement[] = [];

  /** Current set of rendered node IDs */
  private renderedNodeIds: Set<string> = new Set();

  /** Set of node IDs that were just created this frame (need forced content update) */
  private newlyCreatedNodes: Set<string> = new Set();

  /** HTML layer access for renderers */
  private layerAccess: HTMLLayerAccess;

  /** Dummy SVG layer access (renderers may use SVG layer) */
  private dummySVGAccess: SVGLayerAccess = {
    addElement: () => {},
    removeElement: () => {},
  };

  /** WebGL layer access for renderers (Phase 0.7+) */
  private webglAccess: WebGLLayerAccess | null = null;

  /** Render options for renderers */
  private renderOptions: RenderOptions = {
    simplifyLOD: true,
    forceTextureAtDistance: false,
    textureThreshold: 60,
    lodFullThreshold: 150,
    lodTitleThreshold: 60,
    lodIconThreshold: 20,
  };

  constructor(container: HTMLElement) {
    this.container = container;
    this.container.classList.add(CSS.layer);
    this.setupContainerStyles();
    this.transformLayer = this.createTransformLayer();

    // Create layer access for renderers
    this.layerAccess = createHTMLLayerAccess(this);
  }

  /**
   * Set the WebGL layer for texture rendering (Phase 0.7+).
   */
  setWebGLLayer(layer: WebGLLayer | null): void {
    this.webglAccess = createWebGLLayerAccess(layer);
  }

  /**
   * Set render options that affect rendering behavior.
   * Tracks changes to force re-render when options change.
   */
  setRenderOptions(options: Partial<RenderOptions>): void {
    const prev = this.renderOptions;
    this.renderOptions = { ...this.renderOptions, ...options };

    // Check if options actually changed
    if (
      prev.simplifyLOD !== this.renderOptions.simplifyLOD ||
      prev.forceTextureAtDistance !==
        this.renderOptions.forceTextureAtDistance ||
      prev.textureThreshold !== this.renderOptions.textureThreshold ||
      prev.lodFullThreshold !== this.renderOptions.lodFullThreshold ||
      prev.lodTitleThreshold !== this.renderOptions.lodTitleThreshold ||
      prev.lodIconThreshold !== this.renderOptions.lodIconThreshold ||
      prev.enableLodFull !== this.renderOptions.enableLodFull ||
      prev.enableLodTitle !== this.renderOptions.enableLodTitle ||
      prev.enableLodIcon !== this.renderOptions.enableLodIcon ||
      prev.enableLodDot !== this.renderOptions.enableLodDot ||
      prev.forceLod !== this.renderOptions.forceLod ||
      prev.forceTextureRendering !== this.renderOptions.forceTextureRendering
    ) {
      this.scheduler.onOptionsChange();
    }
  }

  private setupContainerStyles(): void {
    // Container clips content and establishes coordinate system
    this.container.style.position = "relative";
    this.container.style.overflow = "hidden";
    this.container.style.width = "100%";
    this.container.style.height = "100%";

    // Prevent browser-initiated scroll (e.g. contentEditable focus changes
    // can scroll overflow:hidden containers, shifting the canvas).
    this.container.addEventListener("scroll", () => {
      this.container.scrollLeft = 0;
      this.container.scrollTop = 0;
    });
  }

  private createTransformLayer(): HTMLElement {
    const layer = document.createElement("div");
    layer.classList.add(CSS.transform);

    // Transform layer is positioned at viewport center
    // and receives the camera transform
    layer.style.position = "absolute";
    layer.style.top = "0";
    layer.style.left = "0";
    layer.style.width = "0";
    layer.style.height = "0";
    layer.style.transformOrigin = "0 0";
    layer.style.willChange = "transform";

    this.container.appendChild(layer);
    return layer;
  }

  /**
   * Set the camera for world → screen transforms.
   * Updates the CSS transform on the container.
   * Tracks zoom changes for LOD recalculation.
   */
  setCamera(camera: Camera): void {
    // Notify scheduler of zoom change (it tracks lastRenderedZoom internally)
    this.scheduler.onZoomChange(camera.zoom);
    this.camera = camera;
    this.updateTransform();
  }

  /**
   * Set viewport dimensions.
   */
  setViewportSize(width: number, height: number): void {
    this.viewport = { width, height };
    this.updateTransform();
  }

  /**
   * Update the CSS transform based on camera and viewport.
   *
   * Transform chain:
   * 1. Translate to viewport center
   * 2. Scale by zoom
   * 3. Translate by negative camera position
   *
   * This places world origin (0,0) at viewport center when camera is at (0,0).
   */
  private updateTransform(): void {
    const { x, y, zoom } = this.camera;
    const { width, height } = this.viewport;

    // Transform: move to center, scale, then offset by camera
    // This is equivalent to: screenPos = (worldPos - camera) * zoom + viewportCenter
    this.transformLayer.style.transform = `translate(${width / 2}px, ${height / 2}px) scale(${zoom}) translate(${-x}px, ${-y}px)`;
  }

  /**
   * Compute a fast hash for node data.
   * Used for change detection - not cryptographic.
   */
  private hashNodeData(data: Record<string, unknown>): string {
    // Fast hash for common cases - just use stringified length + first/last chars
    // Full JSON.stringify is expensive, so we do a quick approximation
    const str = JSON.stringify(data);
    const len = str.length;
    if (len < 20) return str; // Short data, use as-is
    // Use length + sample of characters for longer data
    return `${len}:${str.charCodeAt(0)}:${str.charCodeAt(len >> 1)}:${str.charCodeAt(len - 1)}`;
  }

  /**
   * Build render state for a node.
   */
  private buildNodeState(node: UINode, element: HTMLElement): NodeRenderState {
    const currentLod = element.dataset.lod as LODLevel | undefined;
    const isTextureRendered = element.classList.contains("texture-cached");

    return {
      positionX: node.position.x,
      positionY: node.position.y,
      width: node.size.width,
      height: node.size.height,
      z: node.z,
      type: node.type,
      selected: node.selected ?? false,
      hovered: node.hovered ?? false,
      dragging: node.dragging ?? false,
      resizing: node.resizing ?? false,
      className: node.className,
      dataHash: this.hashNodeData(node.data),
      lod: currentLod,
      isTextureRendered,
      rotation: node.rotation ?? 0,
    };
  }

  /**
   * Render nodes to the HTML layer.
   *
   * Optimized for performance:
   * 1. Only removes elements for nodes that left the viewport
   * 2. Only creates elements for new nodes
   * 3. Only updates nodes that have actually changed
   * 4. Uses RenderScheduler for render decisions
   */
  renderNodes(nodes: UINode[]): void {
    const newNodeIds = new Set(nodes.map((n) => n.id));

    // Remove elements for nodes that are no longer visible
    for (const nodeId of this.renderedNodeIds) {
      if (!newNodeIds.has(nodeId)) {
        this.removeNodeElement(nodeId);
      }
    }

    // Update or create elements for current nodes
    for (const node of nodes) {
      this.renderNode(node);
    }

    this.renderedNodeIds = newNodeIds;

    // End the frame - clear per-frame flags and update lastRenderedZoom
    this.scheduler.endFrame(this.camera.zoom);
  }

  /**
   * Render a single node using the RenderScheduler for decisions.
   */
  private renderNode(node: UINode): void {
    let element = this.nodeElements.get(node.id);
    let isNewElement = false;

    if (!element) {
      element = this.acquireElement();
      element.dataset.nodeId = node.id;
      this.nodeElements.set(node.id, element);
      this.transformLayer.appendChild(element);
      isNewElement = true;
      // Track that this node needs forced content update
      this.newlyCreatedNodes.add(node.id);
    }

    // Build current state for render decision
    const currentState = this.buildNodeState(node, element);

    // Use scheduler to compute render decision
    const decision = this.scheduler.computeDecision(
      node.id,
      isNewElement,
      currentState,
    );

    if (!decision.updateLayout && !decision.callRenderer) {
      return;
    }

    this.updateNodeElement(
      element,
      node,
      decision.updateLayout,
      decision.callRenderer,
    );

    // Update cached state in scheduler (only when layout changed)
    if (decision.updateLayout) {
      this.scheduler.updateNodeState(node.id, currentState);
    }

    // Clear the newly created flag after rendering
    // (set for genuinely new elements AND for invalidated nodes)
    this.newlyCreatedNodes.delete(node.id);
  }

  /**
   * Update a node element's position and state.
   * Positions are in WORLD coordinates - the transform layer handles conversion.
   *
   * @param element The DOM element
   * @param node The node data
   * @param updateLayout Whether to update position/size (node moved or new)
   * @param callRenderer Whether to call the renderer (for content or texture batch)
   */
  private updateNodeElement(
    element: HTMLElement,
    node: UINode,
    updateLayout: boolean,
    callRenderer: boolean,
  ): void {
    // Only update layout properties if node position/size changed
    if (updateLayout) {
      // Position in world coordinates (top-left corner)
      element.style.left = `${node.position.x}px`;
      element.style.top = `${node.position.y}px`;
      element.style.width = `${node.size.width}px`;
      element.style.height = `${node.size.height}px`;
      element.style.zIndex = `${node.z}`;

      // Apply rotation transform if specified
      if (node.rotation !== undefined && node.rotation !== 0) {
        element.style.transform = `rotate(${node.rotation}rad)`;
      } else {
        element.style.transform = "";
      }

      // Update data attributes
      element.dataset.type = node.type;

      // Update custom class if provided
      if (node.className) {
        element.className = `${CSS.node} ${node.className}`;
      } else if (!element.classList.contains(CSS.node)) {
        element.classList.add(CSS.node);
      }
    }

    // Always update visual state classes (selected, hovered, dragging, resizing)
    // These can change without layout changes and should always be in sync
    element.classList.toggle(CSS.selected, node.selected ?? false);
    element.classList.toggle(CSS.hovered, node.hovered ?? false);
    element.classList.toggle(CSS.dragging, node.dragging ?? false);
    element.classList.toggle(CSS.resizing, node.resizing ?? false);
    element.classList.toggle(CSS.rotating, node.rotating ?? false);

    // Call renderer (for content update or texture batch submission)
    if (callRenderer) {
      this.renderNodeContent(element, node);
    }
  }

  /**
   * Render node content using type-specific renderers.
   * Renderers handle LOD (Level of Detail) based on screen metrics.
   */
  private renderNodeContent(element: HTMLElement, node: UINode): void {
    const renderer = rendererRegistry.get(node.type);

    if (renderer) {
      // Calculate screen metrics for LOD decisions
      const screenMetrics = this.calculateScreenMetrics(node);

      // Create render layers
      const layers: RenderLayers = {
        html: this.layerAccess,
        svg: this.dummySVGAccess,
        webgl: this.webglAccess,
        options: this.renderOptions,
      };

      // Render using type-specific renderer
      renderer.render(node, screenMetrics, layers);
    } else {
      // Fallback: simple text content if no renderer registered
      const label = (node.data.label as string) || node.type;
      if (element.textContent !== label) {
        element.textContent = label;
      }
    }
  }

  /**
   * Calculate screen metrics for a node.
   * Used by renderers for LOD decisions.
   */
  private calculateScreenMetrics(node: UINode): ScreenMetrics {
    // Screen size is world size * zoom
    return {
      width: node.size.width * this.camera.zoom,
      height: node.size.height * this.camera.zoom,
      zoom: this.camera.zoom,
    };
  }

  /**
   * Remove a node element and return it to the pool.
   */
  private removeNodeElement(nodeId: string): void {
    const element = this.nodeElements.get(nodeId);
    if (element) {
      element.remove();
      this.releaseElement(element);
      this.nodeElements.delete(nodeId);
    }
    // Clean up cached state in scheduler
    this.scheduler.removeNodeState(nodeId);
  }

  /**
   * Acquire an element from the pool or create a new one.
   */
  private acquireElement(): HTMLElement {
    let element = this.elementPool.pop();

    if (!element) {
      element = document.createElement("div");
      element.classList.add(CSS.node);
    }

    return element;
  }

  /**
   * Release an element back to the pool.
   */
  private releaseElement(element: HTMLElement): void {
    // Reset element state
    element.textContent = "";
    element.className = CSS.node;
    delete element.dataset.nodeId;
    delete element.dataset.type;
    element.style.cssText = "";

    // Add to pool (limit pool size to prevent memory bloat)
    if (this.elementPool.length < 100) {
      this.elementPool.push(element);
    }
  }

  /**
   * Invalidate a node's cached render state so it will be re-rendered on the next frame.
   * Used when external code (e.g. inline editing) modifies the node's DOM and needs
   * the renderer to regenerate content even though the node data hasn't changed.
   */
  invalidateNode(nodeId: string): void {
    this.scheduler.removeNodeState(nodeId);
    // Also mark as newly created so renderers with their own content caches
    // (e.g. MarkdownRenderer.lastContent) treat it as fresh and force-update DOM
    this.newlyCreatedNodes.add(nodeId);
  }

  /**
   * Get the DOM element for a node.
   */
  getNodeElement(nodeId: string): HTMLElement | null {
    return this.nodeElements.get(nodeId) ?? null;
  }

  /**
   * Check if a node element was just created this frame.
   * Used by renderers to know when to force content update.
   */
  isNodeNewlyCreated(nodeId: string): boolean {
    return this.newlyCreatedNodes.has(nodeId);
  }

  /**
   * Get node ID at a screen point (for hit testing).
   * Returns the topmost node (highest z-index).
   */
  getNodeAtPoint(screenX: number, screenY: number): string | null {
    // Use elementsFromPoint for accurate hit testing
    const elements = document.elementsFromPoint(screenX, screenY);

    for (const el of elements) {
      if (el instanceof HTMLElement && el.dataset.nodeId) {
        return el.dataset.nodeId;
      }
    }

    return null;
  }

  /**
   * Clean up resources.
   */
  destroy(): void {
    // Remove all node elements
    for (const element of this.nodeElements.values()) {
      element.remove();
    }
    this.nodeElements.clear();
    this.scheduler.clear();
    this.elementPool = [];
    this.renderedNodeIds.clear();

    // Remove transform layer
    this.transformLayer.remove();

    // Remove container class
    this.container.classList.remove(CSS.layer);
  }
}
